import { API_URL } from "./Config";
import { formatDate, formatDateForFiltering } from "./DateUtils";
import {
  GetLicenseClassificationFilterAsync,
  GetLicenseCreatedByFilterAsync,
  GetLicenseSubClassificationFilterAsync,
  IDocumentsColumnFilterList,
} from "./Documents";
import { IDetailsResponse } from "./IDetailsResponse";
import { GetSiteByProfileId, ISiteDetails } from "./ISiteDetails";

/*
    "licence_id": "24c8e87d-7c61-e211-9eb5-0050568a69e2",
    "reg_no": "P0815-02",
    "licenceprofile_id": "373122fb-8a6d-4f9c-bef1-142252f47d33",
    "licence_type": "Industrial Emissions",
    "licence_name": "Lacpatrick Dairies Limited",
    "date_of_licence": "2013-03-25T00:00:00",
    "ceased_date": null,
    "licence_status": "Licensed",
    "licence_substatus": "Licensable Activity Commenced",
    "licence_application_status": "Replaced",
    "licenceprofile_number": "P0815",
    "urban_areape": null
*/

export interface ILicenceDetails {
  licence_id: string;
  reg_no: string;
  licenceprofile_id: string;
  licence_type: string;
  date_of_licence: string;
  ceased_date: string;
  licence_status: string;
  licence_substatus: string;
  licence_application_status: string;
  licenceprofile_number: string;
  urban_areape: string;
}

export async function GetLicenseDetailsAsync(
  licenceId: string
): Promise<ILicenceDetails> {
  var url = `${API_URL}/Licence/byid?licence_id=${licenceId}`;

  if (process.env.REACT_APP_EPA_ENV === "localhost") {
    url = `${API_URL}/licence-details`;
  }

  var response = await fetch(url);
  var body = <ILicenceDetails>await response.json();

  return <ILicenceDetails>{
    licence_id: body.licence_id,
    reg_no: body.reg_no,
    licenceprofile_id: body.licenceprofile_id,
    licence_type: body.licence_type,
    date_of_licence: formatDate(body.date_of_licence),
    ceased_date: formatDate(body.ceased_date),
    licence_status: body.licence_status,
    licence_substatus: body.licence_substatus,
    licence_application_status: body.licence_application_status,
    licenceprofile_number: body.licenceprofile_number,
    urban_areape: body.urban_areape,
  };
}

export const OpenForSubmissionOptions = {
  NotOpenForAny: 0,
  OpenForSubmissions: 1,
  OpenForObjections: 2,
};

export interface IFullLicenceDetails extends IDetailsResponse {
  licenceid: string;
  regno: string;
  sitecomputedaddress: string;
  organisationname: string;
  authappdate: string;
  propappissued: string;
  licencetype: string;
  authname: string;
  licenceissueddate: string;
  expirydate: string;
  objectionsclosingdate: string;
  latestdecisionlink: string;
  latestdecisiontype: string;
  applicationtype: string;
  initiatedby: string;
  lastatus: string;
  licencestatus: string;
  pdduethisyear: string;
  scheduledpddue: string;
  aascreeningcompleted: string;
  aascreeningresult: string;
  eiascreeningcompleted: string;
  eiascreeningresult: string;
  eiareceiveddate: string;
  eiarincompliance: string;
  submissioncount: string;
  objectioncount: string;
  county: string;
  licenceprofileid: string;
  pregolive: boolean;
  sitename: string;
  siteaddress1: string;
  siteaddress2: string;
  siteaddress3: string;
  sitecity: string;
  siteeircode: string;
  sitenace: string;
  sitenuts: string;
  openforsubmissions: number;
  siteDetails?: ISiteDetails;
  licenceDetails?: ILicenceDetails;
  documentCreatedByFilterValues: IDocumentsColumnFilterList;
  documentClassificationFilterValues: IDocumentsColumnFilterList;
  documentSubclassificationFilterValues: IDocumentsColumnFilterList;
  dateclosedforsubmissions: string;
}

export async function GetFullLicenseDetailsAsync(
  licenceId: string
): Promise<IFullLicenceDetails> {
  var url = `${API_URL}/Licence/fulllbyid?licence_id=${licenceId}`;

  if (process.env.REACT_APP_EPA_ENV === "localhost") {
    url = `${API_URL}/full-licence-details`;
  }

  var response = await fetch(url);
  let body = <IFullLicenceDetails>await response.json();

  const siteDetails = await GetSiteByProfileId(body.licenceprofileid);
  body.siteDetails = siteDetails;

  const licenceDetails = await GetLicenseDetailsAsync(licenceId);
  body.licenceDetails = licenceDetails;

  body.documentCreatedByFilterValues = await GetLicenseCreatedByFilterAsync(
    licenceId
  );
  body.documentClassificationFilterValues =
    await GetLicenseClassificationFilterAsync(licenceId);
  body.documentSubclassificationFilterValues =
    await GetLicenseSubClassificationFilterAsync(licenceId);

  return <IFullLicenceDetails>{
    response: response,
    licenceid: body.licenceid,
    regno: body.regno,
    sitecomputedaddress: body.sitecomputedaddress,
    organisationname: body.organisationname,
    authappdate: formatDate(body.authappdate),
    propappissued: formatDate(body.propappissued),
    licencetype: body.licencetype,
    authname: body.authname,
    licenceissueddate: formatDate(body.licenceissueddate),
    expirydate: formatDate(body.expirydate),
    objectionsclosingdate: formatDate(body.objectionsclosingdate),
    latestdecisionlink: body.latestdecisionlink,
    latestdecisiontype: body.latestdecisiontype,
    applicationtype: body.applicationtype,
    initiatedby: body.initiatedby,
    lastatus: body.lastatus,
    licencestatus: body.licencestatus,
    pdduethisyear: body.pdduethisyear,
    scheduledpddue: body.scheduledpddue,
    aascreeningcompleted: body.aascreeningcompleted,
    aascreeningresult: body.aascreeningresult,
    eiascreeningcompleted: body.eiascreeningcompleted,
    eiascreeningresult: body.eiascreeningresult,
    eiareceiveddate: formatDate(body.eiareceiveddate),
    eiarincompliance: body.eiarincompliance,
    submissioncount: body.submissioncount,
    objectioncount: body.objectioncount,
    county: body.county,
    licenceprofileid: body.licenceprofileid,
    pregolive: body.pregolive,
    sitename: body.sitename,
    siteaddress1: body.siteaddress1,
    siteaddress2: body.siteaddress2,
    siteaddress3: body.siteaddress3,
    sitecity: body.sitecity,
    siteeircode: body.siteeircode,
    sitenace: body.sitenace,
    sitenuts: body.sitenuts,
    openforsubmissions: body.openforsubmissions,
    siteDetails: body.siteDetails,
    licenceDetails: body.licenceDetails,
    documentClassificationFilterValues: body.documentClassificationFilterValues,
    documentCreatedByFilterValues: body.documentCreatedByFilterValues,
    documentSubclassificationFilterValues:
      body.documentSubclassificationFilterValues,
    dateclosedforsubmissions: formatDate(body.dateclosedforsubmissions),
  };
}

export const LicenceTypes = {
  ByProductNotification: "By-Product Notification",
  ByProductRegistration: "By-Product Registration",
  HL: "Historic Landfill Certificate of Authorisation",
  VOC: "Volatile Organic Compound (VOC) Permit",
  CoA: "Certificate of Authorisation",
  CoR: "Certificate of Registration",
  WWDA: "Waste Water Discharge Authorisation",
  DAS: "Dumping at Sea Permit",
  EoW: "End-of-Waste Registration",
  IEL: "Industrial Emissions Licence",
  IPC: "Integrated Pollution Control Licence",
  IPPC: "Integrated Pollution Prevention Control Licence",
  ORP: "Radiological Protection Licence",
  Waste: "Waste Licence",
  Abstraction: "Water Abstraction Registration",
  ETS: "Emission Trading System",
};

export const LicenceApplicationStatuses = {
  ApplicationReceived: "Application Received",
  ApplicationCompliance: "Application Compliance",
  Assessment: "Assessment",
  AuthorisationIssued: "Authorisation Issued",
  AuthorisationReplaced: "Authorisation Replaced",
  BoardReview: "Board Review",
  Certified: "Certified",
  ComplianceFailureRejected: "Compliance Failure / Rejected",
  ConsiderationofObjections: "Consideration of Objections (if any)",
  Deregistered: "Deregistered",
  DeterminedAsByProduct: "Determined as By-product",
  DeterminedAsWaste: "DeterminedAsWaste",
  FinalDecisionDue: "FD Due",
  Licensed: "Licensed",
  NoFurtherAction: "No Further Action",
  ProposedDeterminationDue: "PD Due",
  ProposedDeterminationIssued: "Proposed Determination/Decision Issued",
  Refused: "Refused",
  Registered: "Registered",
  Replaced: "Replaced",
  UnderAssessment: "Under Assessment",
  UnderAssessmentRequestForInformationOutstanding:
    "Under Assessment RFI Outstanding",
  UnderReview: "Under Review",
  Withdrawn: "Withdrawn",
};
